"use client";

/**
 * Notification popover for Scale App and Repos Without Access alerts.
 *
 * Data comes from the connectedGithubOrgs endpoint (hit_secondary_rate_limit,
 * next_scale_app, repos_without_access fields on each installation).
 * Only shown to org admins. Scale App notification requires hit_secondary_rate_limit.
 * Dismiss state is persisted to localStorage keyed by org + content signature
 * (resets automatically when the underlying data changes).
 * Query params ?show_scale_notification=true / ?show_repos_notification=true force-show
 * dismissed notifications for testing/support.
 */
import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
import { atom, useAtom } from "jotai";
import { AlertTriangle, Bell, Info, X } from "lucide-react";
import { cn } from "@repo/blacksmith-ui/cn";
import { Popover, PopoverContent, PopoverTrigger } from "@repo/blacksmith-ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@repo/blacksmith-ui/tooltip";
import { Badge } from "@repo/blacksmith-ui/badge";
import { Button } from "@repo/blacksmith-ui/button";
import { useActiveGithubOrgForUserNonSuspense } from "@/hooks/useConnectedGithubOrgs";
import { useLocalStorage } from "usehooks-ts";
const MAX_REPOS_DISPLAYED = 5;
const AUTO_OPEN_DELAY_MS = 500;

// Atom so auto-open only fires once per page load, even when the component
// remounts (e.g. sidebar collapse/expand swaps instances).
const hasAutoOpenedAtom = atom(false);
const SCALE_APP_INSTALL_URL = "https://github.com/apps/blacksmith-sh/installations/select_target";
interface NotificationFloatingButtonProps {
  icon?: React.ReactNode;
  badge?: number;
  className?: string;
  variant?: "floating" | "sidebar";
  /**
   * Sidebar variant only: renders the trigger as a full-width icon+label row
   * (used by the app-switcher rail, where labels are revealed on hover).
   */
  label?: string;
  expanded?: boolean;
  // Mirrors RailRowContents: the rail's animated width wipe keeps labels in
  // the DOM and clips them, while the icon-collapsible sidebar removes them
  // outright when collapsed.
  hideLabelWhenCollapsed?: boolean;
}
const SIDEBAR_ICON_BUTTON_CLASS = "h-8 w-8 rounded-lg p-1.5 text-neutral-500 hover:bg-black/[0.04] hover:text-neutral-900 hover:!border-transparent dark:text-neutral-400 dark:hover:bg-white/[0.04] dark:hover:text-white dark:hover:!border-transparent cursor-pointer shrink-0 data-[state=open]:!bg-[#0000000f] data-[state=open]:text-neutral-900 data-[state=open]:!border-transparent dark:data-[state=open]:!bg-white/6 dark:data-[state=open]:text-white";

// Mirrors the app-switcher rail nav rows (RAIL_ROW in AppSwitcherRail): the
// darker resting text/icon of a nav item, a hover fill + color, and an
// open-state fill while the popover is open. `border-0` cancels the subtle
// Button variant's resting 2px transparent border so the row height and icon
// centering match the neighboring nav rows exactly.
const SIDEBAR_RAIL_ROW_CLASS = "rail-row flex h-8 w-full cursor-pointer items-center justify-start gap-0 overflow-hidden rounded-md border-0 p-0 transition-none type-body-s-regular text-neutral-800 hover:bg-black/[0.04] hover:text-neutral-950 [&_svg]:text-neutral-600 hover:[&_svg]:text-neutral-900 dark:text-neutral-300 dark:hover:bg-white/[0.04] dark:hover:text-white dark:[&_svg]:text-neutral-400 dark:hover:[&_svg]:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-border)] data-[state=open]:!bg-[#0000000f] data-[state=open]:text-neutral-950 data-[state=open]:[&_svg]:text-neutral-900 dark:data-[state=open]:!bg-white/6 dark:data-[state=open]:text-white dark:data-[state=open]:[&_svg]:text-white";
const DISMISS_BUTTON_CLASS = "rounded p-1 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700 dark:text-neutral-400 dark:hover:bg-white/10 dark:hover:text-white";
const CARD_CLASS = "relative rounded-lg border border-black/10 p-3 text-sm bg-white text-neutral-900 dark:border-white/10 dark:bg-[#2A2C2C] dark:text-white";
const ORG_BADGE_CLASS = "rounded-md border-black/10 bg-neutral-100 font-mono text-neutral-800 dark:border-white/10 dark:bg-neutral-800 dark:text-white";

// Self-end alignment layered onto the secondary Button pill.
const ACTION_LINK_CLASS = "self-end border-black/[0.24] bg-transparent text-neutral-900 hover:border-black/[0.24] hover:bg-neutral-100 dark:border-white/[0.24] dark:text-white dark:hover:border-white/[0.24] dark:hover:bg-white/10";
function NotificationBadge(t0) {
  const $ = _c(10);
  const {
    count,
    size
  } = t0;
  const label = count > 99 ? "99+" : String(count);
  const isSm = size === "sm";
  const wrapperClass = isSm ? "absolute -top-1 -right-1 flex h-[14px] w-[14px] overflow-visible" : "absolute -top-1.5 -right-1.5 flex h-[18px] w-[18px] overflow-visible";
  const badgeClass = isSm ? "min-w-[14px] h-[14px] rounded-full text-[9px]" : "min-w-[18px] h-[18px] rounded-full text-[11px]";
  let t1;
  if ($[0] !== wrapperClass) {
    t1 = cn(wrapperClass, "items-center justify-center");
    $[0] = wrapperClass;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== badgeClass) {
    t2 = cn("relative flex items-center justify-center rounded-full font-medium", "bg-[#202122] text-white dark:bg-white dark:text-[#202122] dark:border dark:border-[#242526]", badgeClass);
    $[2] = badgeClass;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== label || $[5] !== t2) {
    t3 = <span className={t2}>{label}</span>;
    $[4] = label;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t1 || $[8] !== t3) {
    t4 = <span className={t1}>{t3}</span>;
    $[7] = t1;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}

// Countless dot indicator for the rail bell. Positioned over the bell's
// upper-right, sitting well inside the 32px icon slot so the sidebar button's
// overflow-hidden never clips it. Fades out once the rail panel expands (the
// panel is a `group` with data-state), where the pill count takes over.
function NotificationDot() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <span className="pointer-events-none absolute right-[6px] top-[5px] size-[9px] rounded-full border border-sidebar bg-[#202122] transition-opacity duration-200 group-data-[state=expanded]:opacity-0 dark:bg-white" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function NotificationCard(t0) {
  const $ = _c(17);
  const {
    onDismiss,
    icon: Icon,
    iconClassName,
    children
  } = t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cn("absolute right-2 top-2", DISMISS_BUTTON_CLASS);
    t2 = <X className="size-4" />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== onDismiss) {
    t3 = <button type="button" onClick={onDismiss} aria-label="Dismiss notification" className={t1}>{t2}</button>;
    $[2] = onDismiss;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== iconClassName) {
    t4 = cn("size-3.5 mt-[0.2em] shrink-0", iconClassName);
    $[4] = iconClassName;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== Icon || $[7] !== t4) {
    t5 = <Icon className={t4} aria-hidden={true} />;
    $[6] = Icon;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== children) {
    t6 = <div className="flex min-w-0 flex-1 flex-col gap-1.5">{children}</div>;
    $[9] = children;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t5 || $[12] !== t6) {
    t7 = <div className="flex items-start gap-1.5">{t5}{t6}</div>;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t3 || $[15] !== t7) {
    t8 = <div className={CARD_CLASS}>{t3}{t7}</div>;
    $[14] = t3;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  return t8;
}
export function NotificationFloatingButton(t0) {
  const $ = _c(94);
  const {
    icon,
    badge,
    className,
    variant: t1,
    label,
    expanded: t2,
    hideLabelWhenCollapsed: t3
  } = t0;
  const variant = t1 === undefined ? "floating" : t1;
  const expanded = t2 === undefined ? true : t2;
  const hideLabelWhenCollapsed = t3 === undefined ? false : t3;
  const {
    activeNamedOrg,
    isOrgAdmin,
    activeGithubOrgHitSecondaryRateLimit,
    activeGithubOrgNextScaleApp,
    activeGithubOrgReposWithoutAccess
  } = useActiveGithubOrgForUserNonSuspense();
  const repos = activeGithubOrgReposWithoutAccess ?? [];
  const displayedRepos = repos.slice(0, MAX_REPOS_DISPLAYED);
  const remainingCount = repos.length - MAX_REPOS_DISPLAYED;
  const displayOrg = activeNamedOrg?.orgName ?? "useblacksmith";
  const scaleAppInstallUrl = activeGithubOrgNextScaleApp?.install_url ?? SCALE_APP_INSTALL_URL;
  let t4;
  if ($[0] !== activeGithubOrgNextScaleApp?.name) {
    t4 = activeGithubOrgNextScaleApp?.name.replace("scale", "") ?? "";
    $[0] = activeGithubOrgNextScaleApp?.name;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const scaleAppName = t4;
  let forceShowScale;
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    const searchParams = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams();
    forceShowScale = searchParams.get("show_scale_notification") === "true";
    t5 = searchParams.get("show_repos_notification");
    $[2] = forceShowScale;
    $[3] = t5;
  } else {
    forceShowScale = $[2];
    t5 = $[3];
  }
  const forceShowRepos = t5 === "true";
  const scaleAppContentKey = activeGithubOrgNextScaleApp?.name ?? "";
  let t6;
  if ($[4] !== activeGithubOrgReposWithoutAccess) {
    t6 = activeGithubOrgReposWithoutAccess ? activeGithubOrgReposWithoutAccess.slice().sort().join(",") : "";
    $[4] = activeGithubOrgReposWithoutAccess;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const reposContentKey = t6;
  const [scaleAppDismissed, setScaleAppDismissed] = useLocalStorage(`scale-app-notif-dismissed-${activeNamedOrg?.orgName}-${scaleAppContentKey}`, false);
  const [reposDismissed, setReposDismissed] = useLocalStorage(`repos-without-access-notif-dismissed-${activeNamedOrg?.orgName}-${reposContentKey}`, false);
  const [open, setOpen] = useState(false);
  const [hasAutoOpened, setHasAutoOpened] = useAtom(hasAutoOpenedAtom);
  const focusTargetRef = useRef(null);
  let t7;
  if ($[6] !== setReposDismissed || $[7] !== setScaleAppDismissed) {
    t7 = id => {
      if (id === "scale-app") {
        setScaleAppDismissed(true);
      }
      if (id === "repos-without-access") {
        setReposDismissed(true);
      }
    };
    $[6] = setReposDismissed;
    $[7] = setScaleAppDismissed;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const dismiss = t7;
  let t8;
  if ($[9] !== setOpen || $[10] !== setReposDismissed || $[11] !== setScaleAppDismissed) {
    t8 = () => {
      setScaleAppDismissed(true);
      setReposDismissed(true);
      setOpen(false);
    };
    $[9] = setOpen;
    $[10] = setReposDismissed;
    $[11] = setScaleAppDismissed;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const clearAll = t8;
  const scaleAppEligible = !!activeNamedOrg && !!isOrgAdmin && !!activeGithubOrgNextScaleApp && !!activeGithubOrgHitSecondaryRateLimit;
  const reposEligible = !!activeNamedOrg && !!isOrgAdmin && repos.length > 0;
  const showScaleApp = !scaleAppDismissed && (forceShowScale || scaleAppEligible);
  const showReposWithoutAccess = !reposDismissed && (forceShowRepos || reposEligible);
  const notificationCount = (showScaleApp ? 1 : 0) + (showReposWithoutAccess ? 1 : 0);
  const displayCount = badge != null ? badge : notificationCount;
  const hasNotifications = displayCount > 0;
  if (open && !hasNotifications) {
    setOpen(false);
  }
  let t9;
  if ($[13] !== hasAutoOpened || $[14] !== hasNotifications || $[15] !== setHasAutoOpened || $[16] !== setOpen || $[17] !== variant) {
    t9 = () => {
      if (variant !== "sidebar" || !hasNotifications || hasAutoOpened) {
        return;
      }
      const openPopover = () => {
        setHasAutoOpened(true);
        setOpen(true);
      };
      let timer;
      const schedule = () => {
        timer = setTimeout(openPopover, AUTO_OPEN_DELAY_MS);
      };
      if (typeof document !== "undefined" && document.readyState === "complete") {
        schedule();
      } else {
        if (typeof window !== "undefined") {
          window.addEventListener("load", schedule);
          return () => {
            window.removeEventListener("load", schedule);
            clearTimeout(timer);
          };
        }
      }
      return () => clearTimeout(timer);
    };
    $[13] = hasAutoOpened;
    $[14] = hasNotifications;
    $[15] = setHasAutoOpened;
    $[16] = setOpen;
    $[17] = variant;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== hasAutoOpened || $[20] !== hasNotifications || $[21] !== setHasAutoOpened || $[22] !== variant) {
    t10 = [variant, hasNotifications, hasAutoOpened, setHasAutoOpened];
    $[19] = hasAutoOpened;
    $[20] = hasNotifications;
    $[21] = setHasAutoOpened;
    $[22] = variant;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  useEffect(t9, t10);
  const isSidebar = variant === "sidebar";
  const popoverAlign = isSidebar ? "start" : "end";
  const popoverSide = isSidebar ? "right" : "top";
  const popoverSideOffset = isSidebar ? 8 : 16;
  const popoverAlignOffset = isSidebar ? -8 : 0;
  let t11;
  if ($[24] !== className || $[25] !== displayCount || $[26] !== expanded || $[27] !== hasNotifications || $[28] !== hideLabelWhenCollapsed || $[29] !== icon || $[30] !== isSidebar || $[31] !== label) {
    t11 = isSidebar ? label ? <Button variant="subtle" aria-disabled={!hasNotifications} className={cn(SIDEBAR_RAIL_ROW_CLASS, className)} aria-label="Notifications"><span className="rail-row-icon relative flex size-8 shrink-0 items-center justify-center"><Bell className="h-[18px] w-[18px]" />{displayCount > 0 && <NotificationDot />}</span><span className={cn("type-body-s-regular whitespace-nowrap text-left", hideLabelWhenCollapsed && !expanded && "hidden")}>{label}</span>{displayCount > 0 && !(hideLabelWhenCollapsed && !expanded) && <span className="ml-auto mr-3 inline-flex h-[16px] min-w-[16px] shrink-0 items-center justify-center rounded-full border border-sidebar bg-[#202122] px-1 text-[11px] font-medium leading-none text-white dark:bg-white dark:text-[#202122]">{displayCount}</span>}</Button> : <Button variant="subtle" size="icon" aria-disabled={!hasNotifications} className={cn(SIDEBAR_ICON_BUTTON_CLASS, "relative", !hasNotifications && "pointer-events-none", className)} aria-label="Notifications"><Bell className="h-[18px] w-[18px]" />{displayCount > 0 && <NotificationDot />}</Button> : <div role="button" tabIndex={hasNotifications ? 0 : -1} aria-label="Notifications" aria-disabled={!hasNotifications} className={cn("fixed bottom-4 right-4 z-50 flex items-center p-2", "bg-white dark:bg-[#242526] border border-black/[0.24] rounded-full", "shadow-[0px_11px_12px_0px_rgba(0,0,0,0.15)]", hasNotifications ? "cursor-pointer" : "opacity-50 pointer-events-none", className)}><span className={cn("relative flex h-8 w-8 items-center justify-center shrink-0 rounded-full border border-neutral-900", "bg-blacksmith-yellow transition-colors hover:brightness-95")}>{icon ?? <Bell className="size-4 text-[#202122]" />}{displayCount > 0 && <NotificationBadge count={displayCount} size="md" />}</span></div>;
    $[24] = className;
    $[25] = displayCount;
    $[26] = expanded;
    $[27] = hasNotifications;
    $[28] = hideLabelWhenCollapsed;
    $[29] = icon;
    $[30] = isSidebar;
    $[31] = label;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  const trigger = t11;
  if (!hasNotifications) {
    const t12 = label && "w-full";
    let t13;
    if ($[33] !== t12) {
      t13 = cn("inline-flex", t12);
      $[33] = t12;
      $[34] = t13;
    } else {
      t13 = $[34];
    }
    let t14;
    if ($[35] !== t13 || $[36] !== trigger) {
      t14 = <TooltipTrigger asChild={true}><span className={t13}>{trigger}</span></TooltipTrigger>;
      $[35] = t13;
      $[36] = trigger;
      $[37] = t14;
    } else {
      t14 = $[37];
    }
    let t15;
    if ($[38] !== popoverSide) {
      t15 = <TooltipContent side={popoverSide}>No new notifications</TooltipContent>;
      $[38] = popoverSide;
      $[39] = t15;
    } else {
      t15 = $[39];
    }
    let t16;
    if ($[40] !== t14 || $[41] !== t15) {
      t16 = <Tooltip>{t14}{t15}</Tooltip>;
      $[40] = t14;
      $[41] = t15;
      $[42] = t16;
    } else {
      t16 = $[42];
    }
    return t16;
  }
  const t12 = isSidebar ? "animate-notification-popover-in-right" : "animate-notification-popover-in";
  let t13;
  if ($[43] !== t12) {
    t13 = cn("w-96 max-w-[calc(100vw-2rem)] p-0 outline-hidden ring-0 rounded-lg", "before:absolute before:bg-transparent before:content-['']", "data-[side=right]:before:-left-3 data-[side=right]:before:top-0 data-[side=right]:before:h-full data-[side=right]:before:w-3", "data-[side=left]:before:-right-3 data-[side=left]:before:top-0 data-[side=left]:before:h-full data-[side=left]:before:w-3", "data-[side=top]:before:-bottom-3 data-[side=top]:before:left-0 data-[side=top]:before:h-3 data-[side=top]:before:w-full", "data-[side=bottom]:before:-top-3 data-[side=bottom]:before:left-0 data-[side=bottom]:before:h-3 data-[side=bottom]:before:w-full", t12);
    $[43] = t12;
    $[44] = t13;
  } else {
    t13 = $[44];
  }
  const popoverClass = t13;
  const headerPadding = isSidebar ? "pl-3 pr-1.5 py-1.5" : "pl-2 pr-1.5 py-2";
  const showCollapsedLabelTooltip = isSidebar && !!label && hideLabelWhenCollapsed && !expanded;
  const T0 = Popover;
  const t14 = false;
  let t15;
  if ($[45] !== label || $[46] !== popoverSide || $[47] !== showCollapsedLabelTooltip || $[48] !== trigger) {
    t15 = showCollapsedLabelTooltip ? <Tooltip><TooltipTrigger asChild={true}><PopoverTrigger asChild={true}>{trigger}</PopoverTrigger></TooltipTrigger><TooltipContent side={popoverSide}>{label}</TooltipContent></Tooltip> : <PopoverTrigger asChild={true}>{trigger}</PopoverTrigger>;
    $[45] = label;
    $[46] = popoverSide;
    $[47] = showCollapsedLabelTooltip;
    $[48] = trigger;
    $[49] = t15;
  } else {
    t15 = $[49];
  }
  const T1 = PopoverContent;
  let t16;
  let t17;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = e => {
      e.preventDefault();
      focusTargetRef.current?.focus();
    };
    t17 = <span ref={focusTargetRef} tabIndex={-1} className="sr-only">Notifications</span>;
    $[50] = t16;
    $[51] = t17;
  } else {
    t16 = $[50];
    t17 = $[51];
  }
  let t18;
  if ($[52] !== headerPadding) {
    t18 = cn("flex items-center justify-between gap-2 border-b border-black/10 dark:border-white/10", headerPadding);
    $[52] = headerPadding;
    $[53] = t18;
  } else {
    t18 = $[53];
  }
  let t19;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <h3 className="font-semibold text-sm text-neutral-900 dark:text-white">Notifications</h3>;
    $[54] = t19;
  } else {
    t19 = $[54];
  }
  let t20;
  if ($[55] !== clearAll) {
    t20 = <button type="button" onClick={clearAll} className="text-xs font-medium text-neutral-600 hover:text-neutral-900 dark:text-neutral-300 dark:hover:text-white">Clear all</button>;
    $[55] = clearAll;
    $[56] = t20;
  } else {
    t20 = $[56];
  }
  let t21;
  if ($[57] !== setOpen) {
    t21 = () => setOpen(false);
    $[57] = setOpen;
    $[58] = t21;
  } else {
    t21 = $[58];
  }
  let t22;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <X className="size-4" />;
    $[59] = t22;
  } else {
    t22 = $[59];
  }
  let t23;
  if ($[60] !== t21) {
    t23 = <button type="button" onClick={t21} aria-label="Close notifications" className={DISMISS_BUTTON_CLASS}>{t22}</button>;
    $[60] = t21;
    $[61] = t23;
  } else {
    t23 = $[61];
  }
  let t24;
  if ($[62] !== t20 || $[63] !== t23) {
    t24 = <div className="flex items-center gap-1">{t20}{t23}</div>;
    $[62] = t20;
    $[63] = t23;
    $[64] = t24;
  } else {
    t24 = $[64];
  }
  let t25;
  if ($[65] !== t18 || $[66] !== t24) {
    t25 = <div className={t18}>{t19}{t24}</div>;
    $[65] = t18;
    $[66] = t24;
    $[67] = t25;
  } else {
    t25 = $[67];
  }
  const t26 = "flex flex-col gap-1.5 p-2 max-h-[400px] overflow-y-auto";
  let t27;
  if ($[68] !== dismiss || $[69] !== displayOrg || $[70] !== scaleAppInstallUrl || $[71] !== scaleAppName || $[72] !== showScaleApp) {
    t27 = showScaleApp && <NotificationCard onDismiss={() => dismiss("scale-app")} icon={AlertTriangle} iconClassName="text-amber-600 dark:text-amber-400"><p className="pr-8">Your GitHub organization{" "}<Badge variant="muted" className={ORG_BADGE_CLASS}>{displayOrg}</Badge>{" "}is hitting GitHub API rate limits, which can slow down runner provisioning. Install Blacksmith Scale App{scaleAppName ? ` ${scaleAppName}` : ""} to resolve this.</p><Button asChild={true} variant="secondary" className={ACTION_LINK_CLASS}><a href={scaleAppInstallUrl} target="_blank" rel="noreferrer">Install</a></Button></NotificationCard>;
    $[68] = dismiss;
    $[69] = displayOrg;
    $[70] = scaleAppInstallUrl;
    $[71] = scaleAppName;
    $[72] = showScaleApp;
    $[73] = t27;
  } else {
    t27 = $[73];
  }
  const t28 = showReposWithoutAccess && <NotificationCard onDismiss={() => dismiss("repos-without-access")} icon={Info} iconClassName="text-blue-600 dark:text-blue-400"><p className="pr-8">Blacksmith is running jobs for{" "}<Badge variant="muted" className={ORG_BADGE_CLASS}>{displayOrg}</Badge>{" "}on {repos.length}{" "}{repos.length === 1 ? "repository" : "repositories"} it isn't receiving GitHub events for, which can delay job pickup. Check that the Blacksmith GitHub App has access to{repos.length > 0 ? <>{" "}<strong>{displayedRepos.join(", ")}</strong>{remainingCount > 0 && ` + ${remainingCount} more`}.</> : " these repositories."}</p><Button asChild={true} variant="secondary" className={ACTION_LINK_CLASS}><a href={SCALE_APP_INSTALL_URL} target="_blank" rel="noreferrer">Review access</a></Button></NotificationCard>;
  let t29;
  if ($[74] !== t27 || $[75] !== t28) {
    t29 = <div className={t26}>{t27}{t28}</div>;
    $[74] = t27;
    $[75] = t28;
    $[76] = t29;
  } else {
    t29 = $[76];
  }
  let t30;
  if ($[77] !== T1 || $[78] !== popoverAlign || $[79] !== popoverAlignOffset || $[80] !== popoverClass || $[81] !== popoverSide || $[82] !== popoverSideOffset || $[83] !== t16 || $[84] !== t17 || $[85] !== t25 || $[86] !== t29) {
    t30 = <T1 align={popoverAlign} side={popoverSide} sideOffset={popoverSideOffset} alignOffset={popoverAlignOffset} className={popoverClass} onOpenAutoFocus={t16}>{t17}{t25}{t29}</T1>;
    $[77] = T1;
    $[78] = popoverAlign;
    $[79] = popoverAlignOffset;
    $[80] = popoverClass;
    $[81] = popoverSide;
    $[82] = popoverSideOffset;
    $[83] = t16;
    $[84] = t17;
    $[85] = t25;
    $[86] = t29;
    $[87] = t30;
  } else {
    t30 = $[87];
  }
  let t31;
  if ($[88] !== T0 || $[89] !== open || $[90] !== setOpen || $[91] !== t15 || $[92] !== t30) {
    t31 = <T0 open={open} onOpenChange={setOpen} modal={t14}>{t15}{t30}</T0>;
    $[88] = T0;
    $[89] = open;
    $[90] = setOpen;
    $[91] = t15;
    $[92] = t30;
    $[93] = t31;
  } else {
    t31 = $[93];
  }
  return t31;
}